import React, { useState } from 'react'
import { Box, Typography, styled } from '@mui/material'
import { Button, FormGroup } from '@/design-system'
import { Alert } from '@/design-system/components/Alert'
import { fireAlertToast } from '@/design-system/alerts'
import { useDeactivateAccount, useDeleteAccount } from '@/hooks/api/dashboard'
import { useAuth } from '@/hooks/common'
import { ConfirmDangerModal } from '../ConfirmDangerModal'

const PanelHead = styled(Box)({ marginBottom: 18 })
const H2 = styled(Typography)({ fontSize: 20, fontWeight: 720, letterSpacing: '-0.025em', color: 'var(--ds-ink)' })
const Sub = styled(Typography)({ fontSize: 13, color: 'var(--ds-ink-subtle)', marginTop: 4 })

const DangerCard = styled(Box)({
  borderRadius: 'var(--ds-radius-md)',
  border: '1.5px solid',
  padding: '18px 20px',
  display: 'flex',
  gap: 14,
  alignItems: 'flex-start',
})

const RowIc = styled(Box)({ width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 })
const T = styled('div')({ fontSize: 14, fontWeight: 680, color: 'var(--ds-ink)' })
const S = styled('div')({ fontSize: 12.5, color: 'var(--ds-ink-muted)', lineHeight: 1.55, margin: '4px 0 12px', maxWidth: 460 })

/** Deactivate/delete, wired to the real `setAccountStatus` endpoints with the same type-to-confirm safety gate the legacy modals used. */
export function DangerZoneSection() {
  const { user, logout } = useAuth()
  const deactivate = useDeactivateAccount(user?.userID ?? '')
  const remove = useDeleteAccount(user?.userID ?? '')
  const [deactivateOpen, setDeactivateOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const handleDeactivate = async () => {
    try {
      await deactivate.mutateAsync()
      fireAlertToast('settings.accountDeactivated')
      logout()
    } catch {
      fireAlertToast('settings.accountDeactivateFailed')
    }
  }

  const handleDelete = async () => {
    try {
      await remove.mutateAsync()
      fireAlertToast('settings.accountDeleted')
      logout()
    } catch {
      fireAlertToast('settings.accountDeleteFailed')
    }
  }

  return (
    <Box>
      <PanelHead>
        <H2>Danger Zone</H2>
        <Sub>Actions here are permanent and cannot be undone.</Sub>
      </PanelHead>

      <Alert
        variant='warning'
        title='Proceed with caution'
        description='Deactivating pauses your account; deleting removes it — and everything in it — for good.'
        sx={{ marginBottom: '18px' }}
      />

      <FormGroup>
        <DangerCard sx={{ borderColor: 'rgba(255,207,125,0.5)', background: 'rgba(255,207,125,0.08)', marginBottom: 2 }}>
          <RowIc sx={{ background: 'rgba(255,207,125,0.25)', color: '#92600a' }}>⏸</RowIc>
          <Box>
            <T>Deactivate account</T>
            <S>Temporarily disable your account. Albums stay intact — reactivate anytime just by logging back in.</S>
            <Button
              size='sm'
              onClick={() => setDeactivateOpen(true)}
              sx={{ boxShadow: 'inset 0 0 0 1px rgba(255,207,125,0.7)', color: '#92600a', backgroundColor: '#fff', '&:hover': { boxShadow: 'inset 0 0 0 1px #92600a' } }}
            >
              Deactivate account
            </Button>
          </Box>
        </DangerCard>

        <DangerCard sx={{ borderColor: 'color-mix(in srgb, var(--ds-accent-deep-a) 40%, transparent)', background: 'color-mix(in srgb, var(--ds-accent-deep-a) 6%, transparent)', marginTop: 2 }}>
          <RowIc sx={{ background: 'color-mix(in srgb, var(--ds-accent-deep-a) 16%, transparent)', color: 'var(--ds-accent-deep-a)' }}>🗑</RowIc>
          <Box>
            <T>Delete account</T>
            <S>Permanently delete your account, albums, keys, and 3D galleries. This cannot be undone.</S>
            <Button
              size='sm'
              onClick={() => setDeleteOpen(true)}
              sx={{ backgroundColor: 'var(--ds-accent-deep-a)', color: '#fff', '&:hover': { backgroundColor: 'var(--ds-accent-deep-a)', opacity: 0.88 } }}
            >
              Delete account
            </Button>
          </Box>
        </DangerCard>
      </FormGroup>

      <ConfirmDangerModal
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        onConfirm={handleDeactivate}
        loading={deactivate.isLoading}
        title='Deactivate account'
        description='Your account will be hidden and paused. You can reactivate it anytime just by logging back in.'
        confirmPhrase='DEACTIVATE'
        actionLabel='Deactivate account'
        tone='amber'
      />
      <ConfirmDangerModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        loading={remove.isLoading}
        title='Delete account'
        description='This permanently deletes your account, albums, keys, and 3D galleries. This cannot be undone.'
        confirmPhrase='DELETE MY ACCOUNT'
        actionLabel='Delete account'
        tone='coral'
      />
    </Box>
  )
}
